import { Component, HostListener, NgZone, OnInit, ViewChild, } from "@angular/core";
import { Account, AccountResource, Assignment, AssignmentResource, Binder, BinderResource, Contact, ContactResource, EventResource, HolidayResource, MessagingSettings, NotificationResource, PinRequest, SettingsResource, Shift, ShiftResource, Task } from "@apaq/orgkit-js";
import { addDays, differenceInMinutes, endOfDay, format, isSameDay, isToday, isWithinInterval, startOfDay } from "date-fns";
import { PageRequest } from "@apaq/leap-data-core";
import { DropData, TimelineComponent } from "./timeline/timeline.component";
import { TimelineDataSource } from "./timeline-datasource";
import { PostponeDialogResult, PostponePlanDialogComponent } from "../../postpone-plan-dialog/postpone-plan-dialog.component";
import { DialogService } from "src/app/modules/dialog/dialog.service";
import { DownloadWorksheetDialogComponent } from "../../download-worksheet-dialog/download-worksheet-dialog.component";
import { Data, EditRecurringAssignmentDialogComponent, TITLE_RECURRING_PLURAL, TITLE_RECURRING_SINGULAR } from "../../edit-recurring-assignment-dialog/edit-recurring-assignment-dialog.component";
import { first, take } from "rxjs/operators";
import { TimeWindowHelper } from "src/app/common/time/timewindow-helper";
import log from 'loglevel';
import { AssignmentHelper } from "src/app/common/assignment-helper";
import { DownloadInvoicesDialogComponent } from "../../download-invoices-dialog/download-invoices-dialog.component";
import { MoveAssignmentsDialogComponent, MoveAssignmentsDialogResult } from "../../move-assignments-dialog/move-assignments-dialog.component";
import { Subject } from "rxjs";
import { bufferDebounce } from "src/app/common/observables/debounce-buffer";
import { MapDatasource } from "./map-datasource";
import { HttpClient } from "@angular/common/http";
import { DataSource } from "src/app/common/scheduler/datasource";
import { DateEvent } from "src/app/common/scheduler/date-event";
import { ContextService } from "ngx-orgkit";


@Component({
  selector: 'ws-assignment-map',
  templateUrl: './assignment-map.component.html',
  styleUrls: ['./assignment-map.component.scss']
})
export class AssignmentMapComponent implements OnInit {

  private eventObservable = new Subject<void>();
  assignments?: Assignment[];
  accounts: Account[] = [];
  contacts?: Contact[];
  groups: Binder[] = [];
  timelineDataSource: DataSource;
  mapDataSource: MapDatasource;
  selectableWorkplan = false;
  currentResourceId?: string;
  currentDate?: Date;
  currentAssignment?: Assignment;
  currentShift?: Shift;
  selectedAssignments?: string[];
  editing = false;
  messagingSettings?: MessagingSettings;
  @ViewChild(TimelineComponent) timeline?: TimelineComponent;

  constructor(private dialogService: DialogService, private readonly zone: NgZone,
    http: HttpClient, 
    private _assignmentResource: AssignmentResource,
    private _accountResource: AccountResource,
    private _holidayResource: HolidayResource,
    private _shiftResource: ShiftResource,
    private _eventResource: EventResource,
    private _notificationResource: NotificationResource,
    private _settingsResource: SettingsResource,
    private _contactResource: ContactResource,
    private _binderResource: BinderResource,
    private context: ContextService) {
    this.mapDataSource = new MapDatasource(http);
    this.timelineDataSource = new TimelineDataSource(this._accountResource, this._assignmentResource, this._shiftResource, this._holidayResource, this._eventResource, context);

    this.handlePlanChanged = this.handlePlanChanged.bind(this);
    this.handlePlanningCompleted = this.handlePlanningCompleted.bind(this);
    this.handleAssignmentChangedEvent = this.handleAssignmentChangedEvent.bind(this);
  }

  ngOnInit() {
    this._settingsResource.findById('messaging').then(s => this.messagingSettings = s as MessagingSettings);
    this._binderResource.findAll({ size: 200, sort: 'name' }).then(result => this.groups = result.content);

    this._eventResource.stream().on('assignment.plan-changed', this.handlePlanChanged);
    this._eventResource.stream().on('planning.completed', this.handlePlanningCompleted);
    this._eventResource.stream().on('assignment.updated', this.handleAssignmentChangedEvent);
    
    this.eventObservable.pipe(bufferDebounce(500)).subscribe(() => this.zone.run(() => this.loadAssignments()));
  }

  handlePlanChanged(ev: any) {
    let relevant = false;
    const changeDetails = ev.data as any[];
    changeDetails.forEach(e => {
      if ((e.previousDate != null && isSameDay(e.previousDate, this.currentDate!)) ||
        (e.newDate != null && isSameDay(e.newDate, this.currentDate!))) {
        relevant = true;
      }
    })

    if (relevant) {
      this.eventObservable.next();
    }
  }

  handlePlanningCompleted(ev: any) {
    const start = ev.data.from as Date;
    const end = ev.data.to as Date;
    let relevant = isWithinInterval(this.currentDate!, { start, end });

    if (relevant) {
      this.eventObservable.next();
    }
  }

  handleAssignmentChangedEvent(ev: any) {
    const changedId = ev.data.id as string;
    let relevant = this.assignments?.some(a => a.id === changedId);

    if (relevant) {
      this.eventObservable.next();
    }
  }

  @HostListener('window:click', ['$event'])
  clickEvent(event: MouseEvent) {
    log.debug('Global click event emitted: ', event);
    if (!this.editing) {
      this.currentAssignment = null!;
    }
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    log.debug('Global keyboard event emitted: ', event);

    if (event.key === 'Escape') {
      this.currentAssignment = null!;
      this.editing = false;
    }
  }

  get totalStops() {
    return this.assignments?.length ?? 0;
  }

  get totalAmount() {
    return this.assignments?.filter(a => a.action !== 'Skipped').map(a => AssignmentHelper.resolveTotalAmount(a)).reduce((p, c) => p + c, 0) ?? 0;
  }

  get totalAmountCompleted() {
    return this.assignments?.filter(a => a.action === 'Completed').map(a => AssignmentHelper.resolveTotalAmount(a)).reduce((p, c) => p + c, 0) ?? 0;
  }

  get totalDuration() {
    const workDuration = this.assignments?.map(a => AssignmentHelper.resolveTotalDuration(a)).reduce((p, c) => p + c, 0) ?? 0;
    const drivingDuration = (this.mapDataSource?.duration ?? 0) / 60;
    return workDuration + drivingDuration;
  }

  get totalDistance() {
    return this.mapDataSource?.distance ?? 0;
  }

  get distanceOut() {
    return this.mapDataSource?.distanceOut ?? 0;
  }

  get distanceBetween() {
    return this.mapDataSource?.distanceBetween ?? 0;
  }

  get distanceHome() {
    return this.mapDataSource?.distanceHome ?? 0;
  }


  get currency() {
    return 'DKK';
  }

  private loadAssignments() {
    log.debug('load assignments');
    this.selectableWorkplan = false;

    if(this.currentShift == null) {
      this.contacts = [];
      this.assignments = [];
      return;
    }

    if (this.currentResourceId != null && this.currentDate != null) {
      const promises: Promise<Assignment>[] = [];


      const p1 = this._assignmentResource?.findAll({
        size: 100,
        sort: 'plan.executionTime', query: {
          'plan.executionTime': [format(startOfDay(this.currentDate), 'yyyy-MM-dd\'T\'HH:mm:ss'), format(endOfDay(this.currentDate), 'yyyy-MM-dd\'T\'HH:mm:ss')],
          'accountId': this.currentResourceId,
          'plan.allDay': false
        }
      } as PageRequest);

      const p2 = this._assignmentResource?.findAll({
        size: 100,
        sort: 'plan.executionTime', query: {
          'plan.executionTime': [format(startOfDay(new Date(0)), 'yyyy-MM-dd\'T\'HH:mm:ss'), format(startOfDay(addDays(this.currentDate, 1)), 'yyyy-MM-dd\'T\'HH:mm:ss')],
          'plan.completionDueTime': [format(startOfDay(addDays(this.currentDate, 1)), 'yyyy-MM-dd\'T\'HH:mm:ss'), format(new Date('2099-12-31'), 'yyyy-MM-dd\'T\'HH:mm:ss')],
          'accountId': this.currentResourceId,
          'plan.allDay': true
        }
      } as PageRequest);
      Promise.all([p1, p2]).then(async results => {
        const assignments = [...results[0].content, ...results[1].content];
        const contactIds = assignments.map(a => a.contactId);
        this.contacts = (await this._contactResource.findAll({ size: 100, query: { id: contactIds } } as PageRequest)).content;
        this.assignments = assignments;

        this.mapDataSource?.loadRoute(this.assignments, this.currentShift!);
      });
    }
  }

  async onLockAssignment() {
    this.doLockAssignments([this.currentAssignment!.id], true);
  }


  async onUnlockAssignment() {
    this.doLockAssignments([this.currentAssignment!.id], false);
  }

  async onLockAssignments() {
    const assignmentIds = this.assignments?.
      filter(a => a.plan.affixment.state !== 'Locked' && !a.plan.allDay && a.action === 'None' && (!this.selectableWorkplan || this.selectedAssignments?.includes(a.id))).
      map(a => a.id);
    this.doLockAssignments(assignmentIds!, true);
  }


  async onUnlockAssignments() {
    const assignmentIds = this.assignments?.
      filter(a => a.plan.affixment.state === 'Locked' && !a.plan.allDay && a.action === 'None' && (!this.selectableWorkplan || this.selectedAssignments?.includes(a.id))).
      map(a => a.id);
    this.doLockAssignments(assignmentIds!, false);
  }

  onAddNewAssignment() {
    this.editing = true;
  }

  onDownloadWorksheets() {
    this.dialogService.open(DownloadWorksheetDialogComponent, $localize`Download Worksheets`, { data: { date: this.currentDate, resourceId: this.currentResourceId } });
  }

  onMenuMoreSelect(event: CustomEvent) {
    const item = event.detail.item;
    switch (item.value) {
      case 'complete':
        this.doCompleteAssignments();
        break;
      case 'notify':
        this.doNotifyAssignments();
        break;
      case 'move':
        this.onMoveAssignments();
        break;
      case 'postpone':
        this.onPostponePlan();
        break;
      case 'affix':
        this.doAffixAssignments();
        break;
      case 'detach':
        this.doDetachAssignments();
        break;
      case 'lock':
        this.onLockAssignments();
        break;
      case 'unlock':
        this.onUnlockAssignments();
        break;
      case 'download-invoices':
        this.onDownloadInvoices();
        break;
      case 'download-worksheets':
        this.onDownloadWorksheets();
        break;
      case 'select':
        this.selectableWorkplan = !this.selectableWorkplan;
        break;
    }
  }

  onWorkplanSelectionChange(selectedAssignments: string[]) {
    this.selectedAssignments = this.selectableWorkplan ? selectedAssignments : undefined;
  }

  onDateClick(props: any) {
    log.debug('unoccupied date clicked', props);
    this.currentAssignment = undefined;
    this.assignments = [];
    this.currentDate = props.date;
    this.currentResourceId = props.resourceId;
    this.timeline?.select(null!);

    this.editing = true;
  }

  onEditCanceled() {
    log.debug('Edit Canceled');
    this.editing = false;
    this.currentAssignment = undefined;
  }

  onEditSaved() {
    log.debug('Edit Saved');
    this.editing = false;
    this.currentAssignment = undefined;
  }

  onPostponePlan() {
    const dialog = this.dialogService.open(PostponePlanDialogComponent, $localize`Postpone Plan`);
    dialog.afterClosed().subscribe(async (result?: PostponeDialogResult) => {
      if (result != null) {
        const confirmed = await Stallion.confirm($localize`BEWARE: Are you absolutely sure you want to postpone the entire plan? (This cannot be undone)`, 'danger', $localize`Cancel`, $localize`Yes, Postpone entire plan`);
        if (confirmed) {
          this._assignmentResource.markDeferred({ type: "AllAssignments", date: result.date, deviation: result.days, assignmentIds: null! });
          Stallion.alert($localize`The assignments are moved in the background. This may take some time.`);
        }
      }
    });
  }

  async onMoveAssignments() {
    const dialog = this.dialogService.open(MoveAssignmentsDialogComponent, $localize`Move assignments`, { data: { date: this.currentDate } });
    const assignments = this.assignments?.filter(a => a.action === 'None' && (!this.selectableWorkplan || this.selectedAssignments?.includes(a.id)));
    const result = (await dialog.afterClosed().pipe(take(1)).toPromise()) as MoveAssignmentsDialogResult;

    if (assignments != null && result != null) {
      this.doPinAssignments(assignments, this.currentResourceId!, result.date);
    }

  }

  async onDownloadInvoices() {
    const ids = this.assignments?.filter(a => a.status === 'Invoiced' || a.status === 'Fulfilled').map(a => a.id);
    if (ids != null) {
      this.dialogService.open(DownloadInvoicesDialogComponent, null!, { data: { assignmentIds: ids }, headerStyle: 'hidden' });
    }
  }

  async onDeleteAssignment() {
    if (this.currentAssignment != null) {
      var name = this.currentAssignment.contactName;
      var result = await Stallion.confirm($localize`Are you sure you want to delete assignment for ${name}?`, 'danger', $localize`Cancel`, $localize`Yes, delete`);
      if (result === true) {
        await this._assignmentResource.delete(this.currentAssignment);
      }
    }
  }

  async doCompleteAssignments() {
    let assignments = this.assignments?.filter(a => (!this.selectableWorkplan || this.selectedAssignments?.includes(a.id)) && a.action !== 'Completed' && a.action != 'Skipped');
    const result = await Stallion.confirm($localize`Do you want to complete ${assignments?.length ?? 0} assignments?`);
    if (assignments != null && result == true) {
      const finalAssignments: Assignment[] = []
      assignments.forEach(async a => {
        if (a.action === "InProgress" && isToday(a.plan.executionTime)) {
          const minutesSincesStart = differenceInMinutes(new Date(), a.plan.executionTime);
          AssignmentHelper.divideMinutesAmongTasks(a, minutesSincesStart);
          finalAssignments.push(await this._assignmentResource.save(a));
        } else if(a.plan.allDay) {
          // ignore
        }  else {
          finalAssignments.push(a);
        }
      });
      this._assignmentResource.markHandle({
        accountId: this.currentResourceId!,
        affectSeries: false,
        entries: finalAssignments.map(a => { return { action: 'Completed', assignmentId: a.id } }),
        timestamp: null! as Date
      })
    }
  }

  async doNotifyAssignments() {
    if (this.messagingSettings?.notificationDeliveryMethods.length == 0) {
      Stallion.alert($localize`No notification methods has been enabled. Go to settings to adjust your notification settings.`);
      return;
    }

    let assignments = this.assignments?.filter(a => (!this.selectableWorkplan || this.selectedAssignments?.includes(a.id)) && !a.plan.allDay && !a.notifiedOfImpendingWork);
    const result = await Stallion.confirm($localize`Do you want to notify ${assignments?.length ?? 0} assignments? (Only contacts with has contact information will be notified)`);
    if (assignments != null && result == true) {
      await this._notificationResource.generateImpendingAssignmentNotification(assignments.map(a => a.id));
      this.loadAssignments();
    }

  }

  async doAffixAssignments() {
    const selectedAssignments = this.assignments?.filter(a => (!this.selectableWorkplan || this.selectedAssignments?.includes(a.id)) && !a.plan.allDay);
    const result = await Stallion.confirm($localize`Do you want to fix servicewindows for ${selectedAssignments?.length ?? 0} assignments?`);
    if (selectedAssignments != null && result == true) {
      await this._assignmentResource.affix({
        assignmentIds: selectedAssignments.map(a => a.id)
      })
    }
  }

  async doDetachAssignments() {
    let assignments = this.assignments?.filter(a => (!this.selectableWorkplan || this.selectedAssignments?.includes(a.id)) && !a.plan.allDay && !TimeWindowHelper.isAllDay(a.plan.serviceWindow));
    const result = await Stallion.confirm($localize`Do you want to detach servicewindows for ${assignments?.length ?? 0} assignments?`);
    if (assignments != null && result == true) {
      await this._assignmentResource.detach({
        assignmentIds: assignments.map(a => a.id)
      })
    }
  }

  async doLockAssignments(assignmentIds: string[], lock: boolean) {
    const promises: Promise<Assignment>[] = [];
    assignmentIds.forEach(id => {
      const promise = this._assignmentResource.save({
        id,
        plan: {
          affixment: {
            state: lock ? 'Locked' : 'Pinned'
          }
        }
      } as Assignment);
      promises.push(promise);
    });
    await Promise.all(promises);
    this.currentAssignment = null!;
    this.loadAssignments();

  }

  onShiftSelected(value: DateEvent & { shift: Shift }) {
    log.info('shift selected: ', value);
    this.currentResourceId = value?.resourceId;
    this.currentDate = value?.start;
    this.currentAssignment = null!;
    this.currentShift = value?.shift;

    this.loadAssignments();
  }

  onAssignmentSelect(assignment: Assignment) {
    if (this.currentAssignment != assignment) {
      this.currentAssignment = assignment;
      log.info('assignment selected: ', this.currentAssignment);
    }
  }

  onWorkplanKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.editing = true;
    }
  }

  async onTimelineDrop(dropData: DropData) {
    log.info('data dropped: ', dropData);
    if (dropData.assignmentIds.length === 0) return;
    const assignments = (await this._assignmentResource.findAll({ size: 100, query: { id: dropData.assignmentIds } } as PageRequest)).content;
    this.doPinAssignments(assignments, dropData.resourceId, dropData.date);
  }

  async doPinAssignments(assignments: Assignment[], resourceId: string, date: Date) {
    const movableAssignments = assignments.filter(a => a.plan.affixment.state !== 'Locked')
    const reccuringAssignments = movableAssignments.filter(a => a.tasks.some(t => t.reference != null));
    const hasReccurringAssignments = reccuringAssignments.length > 0;
    const notifiedCount = movableAssignments.filter(a => a.notifiedOfImpendingWork === true).length;
    const movableAssignmentIds = movableAssignments.map(a => a.id);

    let affectSeries = false;

    if (hasReccurringAssignments) {
      const data: Data = { assignmentCount: reccuringAssignments.length };
      const dialog = this.dialogService.open(EditRecurringAssignmentDialogComponent, reccuringAssignments.length === 1 ? TITLE_RECURRING_SINGULAR : TITLE_RECURRING_PLURAL, { data });
      const result = await dialog.afterClosed().pipe(first()).toPromise();
      if (result == null) return;
      affectSeries = result === 'all';
    }

    if (notifiedCount > 0) {
      if (!await Stallion.confirm($localize`Notifications for ${notifiedCount} of the assignments has already been sent. Are you sure you want to change the assignments?`, 'warning', $localize`Cancel`, $localize`Yes, change assignments`)) {
        return;
      }
    }

    if (movableAssignmentIds.length > 0) {
      const pinRequest: PinRequest = {
        accountId: resourceId,
        date: date,
        dateType: 'Fixed',
        assignmentIds: movableAssignmentIds,
        allowPlanningForFutureOnly: true,
        affectSeries
      };

      this._assignmentResource.pin(pinRequest);
    }
  }

}